/**
 * Renders a list of modules.
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    useMarkConversion,
    type MarkConversionT,
} from "@repo/analytics-learning-time";

import type {DomainSlug} from "#content-types-package/practice-types.ts";

import {applyFilterToItem} from "./content-filters.ts";
import {
    CompressedSeparator,
    NarrowSeparator,
    Separator,
} from "./separators.tsx";
import type {Module} from "./types.ts";
import {transformUnitProgressModule} from "./utils.ts";

/**
 * Possible types of separators between any pair of modules:
 *                  |                 |                 |                 |
 *                  |<- left edge     | centered module |    right edge ->|
 *                  |                 |                 |                 |
 *  full width      -------------------------------------------------------
 *  narrow/centered   ---------------------------------------------------
 *  narrow/right-aligned                ---------------------------------
 *  none
 */
const getSeparator = (
    module1: Module,
    module2: Module,
    key: string,
    compact: boolean,
) => {
    const kind1 = module1.kind;
    const kind2 = module2.kind;

    // When in compact display mode, shrink vertical padding between modules
    if (compact) {
        return <CompressedSeparator key={key} />;
    }

    // So does the PartnershipDescription module. In the rare case that it
    // doesn't have a background, the border is set within the component.
    if (
        kind1 === "PartnershipDescription" ||
        kind2 === "PartnershipDescription"
    ) {
        return null;
    }

    // The subject upsell shelf has a distinct gray background, so separators
    // aren't necessary. The same goes for the Khan Kids Upsell
    if (kind1 === "SubjectUpsellShelf" || kind2 === "SubjectUpsellShelf") {
        return null;
    }

    // Two adjacent BrowseProjects modules should not have a border in between
    if (kind1 === "BrowseProjects" && kind2 === "BrowseProjects") {
        return null;
    }

    // Marks top bound of FloatingSidebar sticky behavior
    if (kind1 === "FloatingSidebar") {
        return <div className="contents_beginSticky" key={key} />;
    }

    // Marks bottom bound of FloatingSidebar sticky behavior
    // TODO(alex): Make invisible once concept pages have a Recommended module
    if (
        (kind1 === "ContentList" || kind1 === "InterspersedQuiz") &&
        kind2 !== "ContentList" &&
        kind2 !== "InterspersedQuiz"
    ) {
        return <Separator key={key} extraClasses="contents_endSticky" />;
    }

    if (kind1 === "PracticeModule" && kind2 !== "PracticeModule") {
        return <Separator key={key} extraClasses="contents_endSticky" />;
    }

    // Full width
    if (
        kind2 === "TabFooter" ||
        kind2 === "PartnershipDescription" ||
        kind1 === "BrowseProjects" ||
        kind2 === "BrowseProjects"
    ) {
        return <Separator key={key} />;
    }

    // Narrow/centered
    if (
        kind1 === "ConceptTags" ||
        kind2 === "ConceptTags" ||
        kind1 === "BrowseProjects" ||
        kind2 === "BrowseProjects" ||
        (kind1 === "ContentCarousel" && kind2 === "ContentCarousel") ||
        (kind1 === "ContentCarousel" && kind2 === "SubjectIntro") ||
        (kind1 === "ContentCarousel" && kind2 === "SubjectExperienceUpsell") ||
        (kind1 === "SubjectIntro" && kind2 === "ContentCarousel") ||
        (kind1 === "SubjectIntro" && kind2 === "SubjectIntro") ||
        (kind1 === "SubjectIntro" && kind2 === "SubjectExperienceUpsell") ||
        (kind1 === "SubjectExperienceUpsell" && kind2 === "ContentCarousel") ||
        (kind1 === "SubjectExperienceUpsell" && kind2 === "SubjectIntro") ||
        (kind1 === "SubjectExperienceUpsell" &&
            kind2 === "SubjectExperienceUpsell") ||
        (kind1 === "TableOfContentsRow" && kind2 === "ContentCarousel")
    ) {
        return <NarrowSeparator alignment="center" key={key} />;
    }

    // Narrow/right-aligned
    // NOTE: ProjectsInProgress uses the same divider, but implements it
    // internally, so it can be omitted if the module is hidden.  If you change
    // this separator, you may need to update that as well.
    if (kind2 === "ContentList" || kind2 === "InterspersedQuiz") {
        return <NarrowSeparator alignment="right" key={key} />;
    }

    return null;
};

/**
 * Return the props to use for the given module. A userInfo field prop is added
 * with relevant data if user data is specified.
 *
 * @param moduleData the set of static data to give to the module.
 * @param userInfo the user-specific set of data for the response as a whole,
 *     or a falsy value if we don't have any user data.
 */
const getModuleProps = function (
    moduleData: Module,
    userInfo?: Record<string, any>,
) {
    if (!userInfo) {
        return moduleData;
    }
    return {
        ...moduleData,
        userInfo: userInfo[moduleData.kind],
    };
};

type OwnProps = {
    backgroundColor?: string;
    // Whether to compress inter-module spacing
    compact: boolean;
    domain: DomainSlug;
    domainId?: string;
    // A map from kinds (strings) to react components. We use this so
    // ModuleList can render modules not necessarily found in the same
    // package.
    moduleComponents: Record<string, React.ComponentType<any>>;
    // Each module will have other properties as well
    modules: Array<Module>;
    titleTag?: string;
    // TODO(bmp): Kill this once Bibliotron colors become standard
    // Whether to use pre-Bibliotron domain colorings
    useOldColors: boolean;
    // Each key of userInfo is a module type, and the value is the
    // user-specific data to provide to that module.
    // TODO(benkraft): Type this.
    userInfo?: Record<string, any>;
    // Callback to call indicating that the page is now loaded enough
    // to be considered usable.
    onComponentInteractive?: () => void;
};

type WithMarkConversion = {
    markConversion: MarkConversionT;
};

type Props = OwnProps & WithMarkConversion;

type DefaultProps = {
    compact: Props["compact"];
    useOldColors: Props["useOldColors"];
};

type State = {
    contentFilterKey: string | null | undefined;
};

class ModuleList extends React.Component<Props, State> {
    static defaultProps: DefaultProps = {
        compact: false,
        useOldColors: false,
    };

    state: State = {
        contentFilterKey: null,
    };

    handleFilterChange: (filter: string) => void = (filter: string) => {
        if (filter !== this.state.contentFilterKey) {
            if (filter === "Video") {
                this.props.markConversion({id: "topic_page_filter_video"});
            } else if (filter === "Practice") {
                this.props.markConversion({id: "topic_page_filter_practice"});
            } else if (filter === "Article") {
                this.props.markConversion({id: "topic_page_filter_article"});
            } else {
                this.props.markConversion({id: "topic_page_filter_all"});
            }
            this.setState({contentFilterKey: filter});
        }
    };

    /**
     * Given a module and its props, apply content filter to its props.
     *
     * @return {?Object} a new object for the filtered props, or, if the content
     *     filter has removed this module completely, returns null
     */
    applyFilterToModuleAndProps: (
        module: Module,
        moduleProps: Module,
    ) => Module | null | undefined = (
        module: Module,
        moduleProps: Module,
    ): Module | null | undefined => {
        const {contentFilterKey} = this.state;

        if (module.kind === "ContentList" && contentFilterKey) {
            // HACK(benkomalo): this is kind of peeking into the internals of
            // ContentList :| but in the absence of a more explicit data store
            // for the content items, I don't see a better way to filter
            // content items.
            const filteredItems = moduleProps.contentItems.filter((item) =>
                applyFilterToItem(contentFilterKey, item),
            );

            if (!filteredItems.length) {
                return null;
            }
            return {
                ...moduleProps,
                contentItems: filteredItems,
            };
        }

        if (module.kind === "TopicProgress") {
            return transformUnitProgressModule(module);
        }
        return moduleProps;
    };

    /**
     * Filter modules that shouldn't be shown and transform their props.
     *
     * This applies the content kind filter across modules, removing modules
     * that are completely removed, as well as transforming props across the
     * modules to remove content items removed by the filter.
     */
    applyInterModuleFilters: (
        modules: Array<Module>,
        baseProps: Array<Module>,
    ) => Array<{
        module: Module;
        moduleProps: Module;
    }> = (modules: Array<Module>, baseProps: Array<Module>) => {
        // `ContentList` modules can be removed completely if they're empty;
        // keep track of which ones are removed so we can remove them the
        // links to those tutorials in other navigational elements (e.g.
        // the FloatingSidebar).
        const hackyMappingOfRemovedContentListModules: Record<string, any> = {};

        let moduleAndProps: Array<{
            module: Module;
            moduleProps: Module;
        }> = [];
        modules.forEach((module: any, i) => {
            const moduleProps = this.applyFilterToModuleAndProps(
                module,
                baseProps[i],
            );

            if (moduleProps) {
                moduleAndProps.push({
                    module,
                    moduleProps,
                });
            } else if (module.kind === "ContentList") {
                hackyMappingOfRemovedContentListModules[baseProps[i].slug] = 1;
            }
        });

        const {contentFilterKey} = this.state;
        if (contentFilterKey) {
            moduleAndProps = moduleAndProps.map(({module, moduleProps}) => {
                if (module.kind === "FloatingSidebar") {
                    return {
                        module,
                        moduleProps: {
                            ...moduleProps,
                            links: moduleProps.links.filter(
                                ({slug}) =>
                                    !hackyMappingOfRemovedContentListModules[
                                        slug
                                    ],
                            ),
                        },
                    };
                }
                return {
                    module,
                    moduleProps,
                };
            });
        }

        return moduleAndProps;
    };

    render(): React.ReactNode {
        const {
            backgroundColor,
            moduleComponents,
            compact,
            domain,
            domainId,
            modules,
            titleTag,
            userInfo,
            useOldColors,
        } = this.props;

        const moduleAndProps = this.applyInterModuleFilters(
            modules,
            // Each module in modules contains a kind prop along with all the module
            // specific props.
            modules.map((module) => getModuleProps(module, userInfo)),
        );

        // We use `reduce` to construct a list of modules and the separators
        // between them.
        const initialAccumulator = {
            _prevModule: null as Module | null | undefined,
            moduleList: [] as Array<React.ReactNode>,
        } as const;
        const addToModuleList = (
            acc: {
                _prevModule: Module | null | undefined;
                moduleList: Array<React.ReactNode>;
            },
            {
                module,
                moduleProps,
            }: {
                module: Module;
                moduleProps: Module;
            },
            i: number,
        ) => {
            // If the separator is `false` or `null`, it won't be rendered.
            const separator =
                !!i &&
                acc._prevModule &&
                getSeparator(acc._prevModule, module, "separator" + i, compact);

            const Module = moduleComponents[module.kind];

            const element = (
                <React.Suspense key={i} fallback={null}>
                    <Module
                        {...moduleProps}
                        domain={domain}
                        domainId={domainId}
                        titleTag={titleTag}
                        useOldColors={useOldColors}
                        onComponentInteractive={
                            this.props.onComponentInteractive
                        }
                    />
                </React.Suspense>
            );

            return {
                _prevModule: module,
                moduleList: acc.moduleList.concat(separator || null, element),
            };
        };
        const {moduleList} = moduleAndProps.reduce(
            addToModuleList,
            initialAccumulator,
        );

        return (
            <div
                className={css(styles.moduleList)}
                style={{
                    backgroundColor: backgroundColor || color.white,
                }}
            >
                {moduleList}
            </div>
        );
    }
}

const styles = StyleSheet.create({
    moduleList: {
        position: "relative",
    },
});

type WithoutMarkConversion<T> = Partial<Omit<T, keyof WithMarkConversion>>;

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof ModuleList,
        React.ComponentProps<typeof ModuleList>
    >
>;

// NOTE(kevinb): We can't use HOCs for this component since some of its
// consumers make use of ref forwarding.  This is because almost all of
// our custom HOCs will not support ref forwarding in the future because
// there isn't a way to make the types work when wrapping a component in
// multiple HOCs.
export default React.forwardRef<ModuleList, ExportProps>((props, ref) => {
    const markConversion = useMarkConversion();

    // @ts-expect-error - TS2769 - No overload matches this call.
    return <ModuleList {...props} markConversion={markConversion} ref={ref} />;
});
